import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { URL_CONFIG } from "@config/urlConfig";
import { createClient, updateClient } from "@actions/fetchClientsAction";
import { useAppConfig } from "@context/AppConfigContext";
import {
  validateField,
  validateForm as validateFormData,
  validateInput,
  hasFormChanged as checkFormChanged,
} from "./addClientFormValidator";

const useAddClientFormData = ({
  onCancel,
  clientData = null,
  onClearFilters,
}) => {
  const isEditMode = Boolean(clientData);

  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [originalFormData, setOriginalFormData] = useState(null);
  const [formData, setFormData] = useState({
    client_name: "",
    opening_cd_balance: 0,
    opening_cd_balance_premium: 0,
    is_active: true,
    maximum_voucher_value:
      URL_CONFIG.VOUCHER_DEFAULT_VALUES.MAXIMUM_VOUCHER_VALUE,
    claim_intimation_timeline:
      URL_CONFIG.VOUCHER_DEFAULT_VALUES.CLAIM_INTIMATION,
    voucher_validity_timeline:
      URL_CONFIG.VOUCHER_DEFAULT_VALUES.VOUCHER_VALIDITY,
  });

  const { user } = useAppConfig();

  // Initialize form with client data in edit mode
  useEffect(() => {
    if (clientData) {
      const initialData = {
        client_name: clientData.client_name || "",
        opening_cd_balance_claim: clientData.opening_cd_balance || 0,
        opening_cd_balance_premium: clientData.opening_cd_balance_premium || 0,
        is_active: clientData.client_status?.toLowerCase() === "active",
        maximum_voucher_value:
          clientData.maximum_voucher_value ||
          URL_CONFIG.VOUCHER_DEFAULT_VALUES.MAXIMUM_VOUCHER_VALUE,
        claim_intimation_timeline:
          clientData.claim_intimation_timeline ||
          URL_CONFIG.VOUCHER_DEFAULT_VALUES.CLAIM_INTIMATION,
        voucher_validity_timeline:
          clientData.voucher_validity_timeline ||
          URL_CONFIG.VOUCHER_DEFAULT_VALUES.VOUCHER_VALIDITY,
      };

      setFormData(initialData);
      // Store original data for change detection
      setOriginalFormData(initialData);
    }
  }, [clientData]);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;

      // Validate input using extracted validator
      const inputValidation = validateInput(name, value);
      if (!inputValidation.isValid) {
        setErrors((prev) => ({
          ...prev,
          [name]: inputValidation.error,
        }));
        return;
      }

      setFormData((prev) => ({ ...prev, [name]: value }));

      if (touchedFields[name]) {
        const error = validateField(name, value);
        setErrors((prev) => ({ ...prev, [name]: error }));
      }
    },
    [touchedFields],
  );

  const handleBlur = useCallback((e) => {
    const { name, value } = e.target;
    setTouchedFields((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  }, []);

  const validateForm = useCallback(() => {
    const validation = validateFormData(formData);
    setErrors(validation.errors);
    setTouchedFields(
      Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
    );
    return validation.isValid;
  }, [formData]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!validateForm()) {
        toast.error("Please fix all errors before submitting");
        return;
      }

      setIsSubmitting(true);

      try {
        if (isEditMode) {
          // Update existing client - Client Name and Opening CD BAlance can't be chnaged
          const payload = {
            clientId: clientData.client_id,
            status: formData.is_active ? "active" : "inactive",
            maximum_voucher_value: formData.maximum_voucher_value.toString(),
            claim_intimation_timeline:
              formData.claim_intimation_timeline.toString(),
            voucher_validity_timeline:
              formData.voucher_validity_timeline.toString(),
          };

          const result = await updateClient(payload);

          if (result.success) {
            toast.success("Client updated successfully!");
            onCancel();
            onClearFilters();
          } else {
            toast.error("Something went wrong, Please try again");
          }
        } else {
          // Create new client
          const payload = {
            client_name: formData.client_name,
            opening_cd_balance_claim: Number(formData.opening_cd_balance),
            opening_cd_balance_premium: Number(
              formData.opening_cd_balance_premium,
            ),
            is_active: formData.is_active,
            maximum_voucher_value: formData.maximum_voucher_value.toString(),
            claim_intimation_timeline:
              formData.claim_intimation_timeline.toString(),
            voucher_validity_timeline:
              formData.voucher_validity_timeline.toString(),
            created_by: {
              id: user?.user?.id || 0,
              email: user?.user?.email || "",
              name: user?.user?.name || "",
              role: user?.user?.role || "",
            },
          };

          const result = await createClient(payload);

          if (result.success) {
            toast.success("Client added successfully!");
            onCancel();
            onClearFilters();
          } else {
            toast.error("Something went wrong, Please try again");
          }
        }
      } catch (error) {
        toast.error(
          error.message || `Failed to ${isEditMode ? "update" : "add"} client`,
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      validateForm,
      isEditMode,
      formData,
      onCancel,
      onClearFilters,
      user,
      clientData,
    ],
  );

  const handleCancel = useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  const getFieldError = useCallback(
    (fieldName) => {
      return touchedFields[fieldName] ? errors[fieldName] : "";
    },
    [errors, touchedFields],
  );

  const hasFieldError = useCallback(
    (fieldName) => {
      return Boolean(touchedFields[fieldName] && errors[fieldName]);
    },
    [errors, touchedFields],
  );

  // Check if form data has changed from original (for edit mode)
  const hasFormChangedValue = useCallback(() => {
    if (!isEditMode || !originalFormData) return false;
    return checkFormChanged(formData, originalFormData);
  }, [formData, originalFormData, isEditMode]);

  return {
    formData,
    errors,
    touchedFields,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    handleCancel,
    getFieldError,
    hasFieldError,
    hasFormChanged: hasFormChangedValue(),
  };
};

export default useAddClientFormData;
